// The depth bands, the current field and the tide of v2 phase 2, as the
// client reads them (v2-the-deep.md §3.1, §5.1, §5.3, R2609, R2616).
//
// The hello carries the band of each cell (`Hello.depth`, one `DepthBand`
// byte a cell) and the current of each cell (`Hello.currents`, an int8
// pair a cell in the unit `CatalogRules.current_unit_px_h`). Both are nil
// while their flag is off, so every reader here answers "no band" and "no
// current" for an empty field, and a canvas piece then draws nothing.
//
// **THE TIDE IS A TABLE LOOKUP.** `CatalogRules.tide_steps` is the factor
// of each equal step of the cycle, and `tide_period_sec` is the cycle on
// the world clock. The step is floor((elapsed mod period) × steps /
// period), the rule of the server (`sim.TideStepAt`, R2595). The client
// mirrors no formula of the table itself: it reads the factor the server
// sends.
//
// Everything here is pure, so a test reads it with no hello.

import { DepthBand } from './gen/proto/aquaphobia';
import { COLS, ROWS, neighborAt } from './world';
import { SEC_PER_DAY, secOfDay } from './locale/clock';

/** The four bands of the water, from the shallowest. */
export const BANDS = [
  DepthBand.DEPTH_BAND_SHELF, DepthBand.DEPTH_BAND_SLOPE,
  DepthBand.DEPTH_BAND_DEEP, DepthBand.DEPTH_BAND_ABYSS,
] as const;

/** The word key of each band, as the locale and the pack spell it. */
export const BAND_KEYS = ['shelf', 'slope', 'deep', 'abyss'] as const;
export type BandKey = (typeof BAND_KEYS)[number];

/** The key of a band, or null for no band (rock, an unsurveyed cell). */
export function bandKey(b: number): BandKey | null {
  return b >= DepthBand.DEPTH_BAND_SHELF && b <= DepthBand.DEPTH_BAND_ABYSS ? BAND_KEYS[b - 1] : null;
}

/** The band of one cell, or NONE for rock, an unsurveyed cell, a cell off
 *  the grid, or a world with no band field. */
export function bandAt(depth: Uint8Array | null, cell: number): number {
  if (!depth || cell < 0 || cell >= depth.length) return DepthBand.DEPTH_BAND_NONE;
  const b = depth[cell];
  return b <= DepthBand.DEPTH_BAND_ABYSS ? b : DepthBand.DEPTH_BAND_NONE;
}

/** The band field of a hello, or null when it carries none or a field of
 *  another grid size. */
export function bandField(bytes: Uint8Array | undefined, cols = COLS, rows = ROWS): Uint8Array | null {
  return bytes && bytes.length > 0 && bytes.length === cols * rows ? bytes : null;
}

/** The current field of a hello as signed pairs, or null when it carries
 *  none or a field of another grid size. The view shares the bytes. */
export function currentField(bytes: Uint8Array | undefined, cols = COLS, rows = ROWS): Int8Array | null {
  if (!bytes || bytes.length === 0 || bytes.length !== 2 * cols * rows) return null;
  return new Int8Array(bytes.buffer, bytes.byteOffset, bytes.length);
}

/** The current of one cell at tide factor 1, in px a map hour, into `out`
 *  as [vx, vy]. A world with no field, and a cell off it, read 0. */
export function currentAt(
  field: Int8Array | null, cell: number, unitPxH: number, out: [number, number],
): [number, number] {
  if (!field || cell < 0 || 2 * cell + 1 >= field.length) { out[0] = 0; out[1] = 0; return out; }
  out[0] = field[2 * cell] * unitPxH;
  out[1] = field[2 * cell + 1] * unitPxH;
  return out;
}

/** The field `strongestCurrent` read last, and its strongest pair. */
let strongestOf: Int8Array | null = null;
let strongestUnits = 0;

/**
 * A survey pass wrote new bands and pairs into the fields (R2626): the
 * strongest current is read again on the next call. `cells` names every
 * cell the pass wrote, and the change log keeps them, so a cache of a
 * field updates the cells alone (R2897). A call without `cells` (a full
 * frame) tells every cache to build whole.
 */
export function fieldChanged(cells?: readonly number[]): void {
  strongestOf = null;
  fieldRevision++;
  if (!cells) { logLost = fieldRevision; return; }
  for (let i = 0; i < cells.length; i++) {
    const at = logHead % FIELD_LOG_CAP;
    // The ring is full: the oldest entry goes, and a reader older than
    // its pass must build whole.
    if (logHead >= FIELD_LOG_CAP && logRevs[at] > logLost) logLost = logRevs[at];
    logCells[at] = cells[i];
    logRevs[at] = fieldRevision;
    logHead++;
  }
}

/** How many times a survey pass wrote into a field: a cache of a field
 *  keys on it beside the field itself (R2626). */
export function fieldRev(): number {
  return fieldRevision;
}
let fieldRevision = 0;

/** The cells the change log holds at most: about a thousand survey passes. */
export const FIELD_LOG_CAP = 8192;
/** The change log of the fields (R2897): a ring of the cells each survey
 *  pass wrote, and the revision of each. It is allocated once. */
const logCells = new Int32Array(FIELD_LOG_CAP);
const logRevs = new Int32Array(FIELD_LOG_CAP);
/** How many entries the log took since the page began. */
let logHead = 0;
/** The newest revision whose cells the log does not hold. */
let logLost = 0;

/**
 * Hands `visit` every cell a survey pass wrote after revision `since`, the
 * newest first; a cell two passes wrote comes twice. Answers false, and
 * visits nothing, when the log does not hold every pass after `since`: a
 * full frame, or more passes than the ring holds. The reader then builds
 * whole. A frame allocates nothing here (§10).
 */
export function fieldCellsSince(since: number, visit: (cell: number) => void): boolean {
  if (since < logLost || since > fieldRevision) return false;
  const oldest = Math.max(0, logHead - FIELD_LOG_CAP);
  for (let k = logHead - 1; k >= oldest; k--) {
    const at = k % FIELD_LOG_CAP;
    if (logRevs[at] <= since) break;
    visit(logCells[at]);
  }
  return true;
}

/** The strongest current of the field at tide factor 1, in px a map hour.
 *  The walk runs once for each field and again after `fieldChanged`. */
export function strongestCurrent(field: Int8Array | null, unitPxH: number): number {
  if (!field) return 0;
  if (field !== strongestOf) {
    let best = 0;
    for (let i = 0; i + 1 < field.length; i += 2) {
      const s = Math.hypot(field[i], field[i + 1]);
      if (s > best) best = s;
    }
    strongestOf = field;
    strongestUnits = best;
  }
  return strongestUnits * unitPxH;
}

/**
 * The step of the tide at `elapsedSec` world seconds into the cycle
 * (`sim.TideStepAt`): 0 for an empty table or no period.
 */
export function tideStepAt(elapsedSec: number, periodSec: number, steps: readonly number[]): number {
  const n = steps.length;
  if (n === 0 || !(periodSec > 0)) return 0;
  const at = ((elapsedSec % periodSec) + periodSec) % periodSec;
  return Math.min(Math.max(Math.floor((at * n) / periodSec), 0), n - 1);
}

/** The factor of the tide at `elapsedSec`: 1 for an empty table, as the
 *  server reads it. */
export function tideFactorAt(elapsedSec: number, periodSec: number, steps: readonly number[]): number {
  return steps.length === 0 ? 1 : steps[tideStepAt(elapsedSec, periodSec, steps)];
}

/** What the tide clock of the legend states (R2616). */
export interface TideClock {
  /** The step now, 0 up. */
  step: number;
  /** The factor of the step now. */
  factor: number;
  /** World seconds until the next step begins. */
  nextInSec: number;
  /**
   * World seconds until the first step whose factor has the other sign:
   * the flow turns round there. -1 when no step of the table turns it.
   */
  turnInSec: number;
}

/** The tide clock at `elapsedSec`, or null for an empty table. */
export function tideClock(elapsedSec: number, periodSec: number, steps: readonly number[]): TideClock | null {
  const n = steps.length;
  if (n === 0 || !(periodSec > 0)) return null;
  const len = periodSec / n;
  const at = ((elapsedSec % periodSec) + periodSec) % periodSec;
  const step = tideStepAt(elapsedSec, periodSec, steps);
  const factor = steps[step];
  const nextInSec = (step + 1) * len - at;
  let turnInSec = -1;
  for (let k = 1; k <= n; k++) {
    const f = steps[(step + k) % n];
    if (f !== 0 && factor !== 0 && Math.sign(f) !== Math.sign(factor)) {
      turnInSec = nextInSec + (k - 1) * len;
      break;
    }
  }
  return { step, factor, nextInSec, turnInSec };
}

/**
 * The world seconds into the tide cycle, from the time of day the clock
 * reads and the time of day the season opened at (R2519). The world clock
 * of the server is the season's elapsed world time, and the time of day is
 * that plus the origin, mod a day. A period that divides the day therefore
 * reads exactly from the clock; any other period reads the season count,
 * which the hello carries to the whole second.
 */
export function tideElapsedSec(
  clockSec: number, originSec: number, periodSec: number, seasonElapsedSec: number,
): number {
  if (periodSec > 0 && SEC_PER_DAY % periodSec === 0) return secOfDay(clockSec - originSec);
  return seasonElapsedSec;
}

/**
 * The hex steps from each water cell to the nearest cell of a shallower
 * band inside its own band (R2616): 1 on a cell that borders a shallower
 * band or rock, more further in, and `cap` where the band holds no edge
 * within `cap` steps. The soundings read it to spread a figure over the
 * span of the band. A cell with no band reads 0. A breadth-first walk,
 * in cell order, once per hello.
 */
export function bandDepthSteps(depth: Uint8Array, cap: number, cols = COLS, rows = ROWS): Uint8Array {
  const n = cols * rows;
  const out = new Uint8Array(n);
  const queue = new Int32Array(n);
  let head = 0, tail = 0;
  for (let c = 0; c < n; c++) {
    const b = depth[c];
    if (b === 0) continue;
    for (let k = 0; k < 6; k++) {
      const nb = neighborAt(c, k, cols, rows);
      if (nb >= 0 && depth[nb] < b) { out[c] = 1; queue[tail++] = c; break; }
    }
  }
  while (head < tail) {
    const c = queue[head++];
    const d = out[c];
    if (d >= cap) continue;
    for (let k = 0; k < 6; k++) {
      const nb = neighborAt(c, k, cols, rows);
      if (nb < 0 || out[nb] !== 0 || depth[nb] !== depth[c]) continue;
      out[nb] = d + 1;
      queue[tail++] = nb;
    }
  }
  for (let c = 0; c < n; c++) if (depth[c] !== 0 && out[c] === 0) out[c] = cap;
  return out;
}

/**
 * Updates the answer of `bandDepthSteps` in place round the cells a survey
 * pass wrote (R2897). The steps of a cell read the bands within `cap - 1`
 * hex steps of it: the walk in its own band reaches `cap - 2` steps, and a
 * cell of the walk borders a shallower band one step further. So `cell`
 * collects every cell within `cap - 1` steps of a written cell, and
 * `finish` walks each one again from itself, level by level in its own
 * band, to the first cell that borders a shallower band or rock. The
 * answer is the answer of a full walk, bit for bit. The stamp planes and
 * the queue are allocated once for a grid size, so a pass allocates
 * nothing (§10).
 */
export class BandStepsPatch {
  /** The cells `finish` walked again, in the order `cell` met them. */
  list = new Int32Array(0);
  /** How many cells of `list` the pass holds. */
  count = 0;
  /** `cell` as a function made once, for `fieldCellsSince`. */
  readonly visit = (c: number): void => { this.cell(c); };

  private seen = new Uint32Array(0);
  private walked = new Uint32Array(0);
  private queue = new Int32Array(0);
  private gen = 0;
  private walkGen = 0;
  private depth: Uint8Array = new Uint8Array(0);
  private steps: Uint8Array = new Uint8Array(0);
  private cap = 1;
  private cols = 0;
  private rows = 0;

  /** Opens a pass over the band field and the steps it answered. */
  begin(depth: Uint8Array, steps: Uint8Array, cap: number, cols = COLS, rows = ROWS): void {
    const n = cols * rows;
    if (this.seen.length !== n) {
      this.seen = new Uint32Array(n);
      this.walked = new Uint32Array(n);
      this.queue = new Int32Array(n);
      this.list = new Int32Array(n);
      this.gen = 0;
      this.walkGen = 0;
    }
    if (this.gen >= 0xfffffffe) { this.seen.fill(0); this.gen = 0; }
    this.gen++;
    this.depth = depth;
    this.steps = steps;
    this.cap = cap;
    this.cols = cols;
    this.rows = rows;
    this.count = 0;
  }

  /** Collects the cells within `cap - 1` steps of a written cell. */
  cell(c: number): void {
    if (c < 0 || c >= this.cols * this.rows) return;
    const seen = this.seen, gen = this.gen, q = this.queue, list = this.list;
    const reach = Math.max(1, this.cap - 1);
    // A ring walk over every cell, rock and band alike: the reach is a
    // hex distance.
    let head = 0, tail = 0;
    if (seen[c] !== gen) { seen[c] = gen; list[this.count++] = c; }
    q[tail++] = c;
    const ring = this.walked;
    if (this.walkGen >= 0xfffffffe) { ring.fill(0); this.walkGen = 0; }
    const own = ++this.walkGen;
    ring[c] = own;
    for (let d = 0; d < reach; d++) {
      const end = tail;
      for (; head < end; head++) {
        const at = q[head];
        for (let k = 0; k < 6; k++) {
          const nb = neighborAt(at, k, this.cols, this.rows);
          if (nb < 0 || ring[nb] === own) continue;
          ring[nb] = own;
          q[tail++] = nb;
          if (seen[nb] !== gen) { seen[nb] = gen; list[this.count++] = nb; }
        }
      }
    }
  }

  /** Walks every collected cell again, and writes its steps. */
  finish(): void {
    for (let i = 0; i < this.count; i++) {
      const c = this.list[i];
      this.steps[c] = this.walk(c);
    }
  }

  /** Does cell `c` border a shallower band or rock? */
  private edge(c: number): boolean {
    const b = this.depth[c];
    for (let k = 0; k < 6; k++) {
      const nb = neighborAt(c, k, this.cols, this.rows);
      if (nb >= 0 && this.depth[nb] < b) return true;
    }
    return false;
  }

  /** The steps of one cell: 1 on an edge cell, the level of the nearest
   *  edge cell of its own band, and `cap` past it; 0 with no band. */
  private walk(c: number): number {
    const depth = this.depth, b = depth[c];
    if (b === 0) return 0;
    if (this.walkGen >= 0xfffffffe) { this.walked.fill(0); this.walkGen = 0; }
    const gen = ++this.walkGen, walked = this.walked, q = this.queue;
    let head = 0, tail = 0;
    q[tail++] = c;
    walked[c] = gen;
    for (let level = 1; level < this.cap; level++) {
      const end = tail;
      for (; head < end; head++) {
        const at = q[head];
        if (this.edge(at)) return level;
        for (let k = 0; k < 6; k++) {
          const nb = neighborAt(at, k, this.cols, this.rows);
          if (nb < 0 || walked[nb] === gen || depth[nb] !== b) continue;
          walked[nb] = gen;
          q[tail++] = nb;
        }
      }
      if (head === tail) break;
    }
    return this.cap;
  }
}
